import type { MetaDeckDetailResponse } from "@openrift/shared/types/api/meta";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { NotFoundFallback, RouteErrorFallback } from "@/components/error-message";
import { Skeleton } from "@/components/ui/skeleton";
import { archivedDeckIdentity } from "@/features/meta/lib/meta-deck-identity";
import { formatRank } from "@/features/meta/lib/meta-format";
import { metaDeckQueryOptions } from "@/features/meta/lib/meta-queries";
import { initQueryOptions } from "@/lib/init-queries";
import { breadcrumbJsonLd, seoHead } from "@/lib/seo";
import { isNotFoundSentinel } from "@/lib/server-fns/api-error";
import { deckShareImageUrl, shareImageVersion } from "@/lib/share-image";
import { getSiteUrl } from "@/lib/site-config";
import { PAGE_WIDTH, PAGE_PADDING, cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/meta_/decks_/$token")({
  head: ({ loaderData, params }) => {
    const siteUrl = getSiteUrl();
    const path = `/meta/decks/${params.token}`;
    const data = loaderData as MetaDeckDetailResponse | undefined;
    if (!data) {
      return seoHead({ siteUrl, title: "Archived deck", path, unlisted: true });
    }
    const finish = formatRank(data.meta.rank, data.meta.rankIsTier);
    const legend = archivedDeckIdentity(data.cards)?.name ?? null;
    const named = legend ?? data.meta.playerName;
    const title = `${named}, ${finish} at ${data.meta.event.name}`;
    const piloted = legend === null ? `this ${data.deck.format} deck` : legend;
    const description = `${data.meta.playerName} piloted ${piloted} to ${finish} at ${data.meta.event.name} (${data.meta.event.eventDate}).`;
    const ogImage = deckShareImageUrl(
      siteUrl,
      params.token,
      shareImageVersion(data.deck.updatedAt),
    );
    return {
      ...seoHead({ siteUrl, title, description, path, ogImage }),
      scripts: [
        breadcrumbJsonLd(siteUrl, [
          { name: "Meta Archive", path: "/meta" },
          { name: data.meta.event.name, path: `/meta/${data.meta.event.slug}` },
          { name: named, path },
        ]),
      ],
    };
  },
  loader: async ({ context, params }): Promise<MetaDeckDetailResponse> => {
    try {
      const [, deck] = await Promise.all([
        context.queryClient.query({ ...initQueryOptions, staleTime: "static" }),
        context.queryClient.query({ ...metaDeckQueryOptions(params.token), staleTime: "static" }),
      ]);
      return deck;
    } catch (error) {
      if (isNotFoundSentinel(error)) {
        throw notFound();
      }
      throw error;
    }
  },
  pendingComponent: MetaDeckPending,
  errorComponent: RouteErrorFallback,
  notFoundComponent: NotFoundFallback,
});

function MetaDeckPending() {
  return (
    <div className={cn(PAGE_PADDING, PAGE_WIDTH.full, "flex flex-col gap-4 py-4")}>
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}
